/**
 * Email Campaigns API
 */
import api from './index'

// Types
export interface EmailList {
  id: string
  name: string
  description?: string
  type: 'static' | 'dynamic'
  filter_criteria?: Record<string, unknown>
  contact_count: number
  member_count?: number  // Alias for contact_count for backwards compatibility
  is_active?: boolean
  created_by?: string
  created_at: string
  updated_at: string
}

export interface EmailTemplate {
  id: string
  name: string
  subject: string
  preview_text?: string
  html_content: string
  json_content?: Record<string, unknown>
  thumbnail_url?: string
  category: 'newsletter' | 'promotional' | 'transactional' | 'announcement' | 'custom'
  is_active: boolean
  created_by?: string
  created_at: string
  updated_at: string
}

export interface EmailCampaign {
  id: string
  name: string
  subject: string
  preview_text?: string
  from_name: string
  from_email: string
  reply_to?: string
  template_id?: string
  html_content?: string
  list_ids: string[]
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled'
  scheduled_at?: string
  sent_at?: string
  company_id?: string
  created_by?: string
  created_at: string
  updated_at: string
  stats?: CampaignStats
}

export interface CampaignStats {
  total_recipients: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  unsubscribed: number
  open_rate: number
  click_rate: number
}

export interface CampaignAnalytics {
  overview: {
    total_campaigns: number
    total_sent: number
    total_delivered: number
    total_opened: number
    total_clicked: number
    avg_open_rate: number
    avg_click_rate: number
  }
  recent_campaigns: Array<{
    id: string
    name: string
    sent_at: string
    recipients: number
    open_rate: number
    click_rate: number
  }>
  trends: {
    dates: string[]
    sent: number[]
    opened: number[]
    clicked: number[]
  }
}

export interface CreateListData {
  name: string
  description?: string
  type?: 'static' | 'dynamic'
  filters?: Record<string, unknown>
}

export interface CreateTemplateData {
  name: string
  subject: string
  preview_text?: string
  html_content: string
  json_content?: Record<string, unknown>
  category?: EmailTemplate['category']
}

export interface CreateCampaignData {
  name: string
  subject: string
  preview_text?: string
  from_name: string
  from_email: string
  reply_to?: string
  template_id?: string
  html_content?: string
  list_ids: string[]
  company_id?: string
}

export const emailCampaignsApi = {
  // Email Lists
  async getLists(): Promise<EmailList[]> {
    const { data } = await api.get('/email-campaigns/lists')
    return data.data
  },

  async getList(listId: string): Promise<EmailList> {
    const { data } = await api.get(`/email-campaigns/lists/${listId}`)
    return data.data
  },

  async createList(listData: CreateListData): Promise<EmailList> {
    const { data } = await api.post('/email-campaigns/lists', listData)
    return data.data
  },

  async updateList(listId: string, listData: Partial<CreateListData>): Promise<EmailList> {
    const { data } = await api.put(`/email-campaigns/lists/${listId}`, listData)
    return data.data
  },

  async deleteList(listId: string): Promise<void> {
    await api.delete(`/email-campaigns/lists/${listId}`)
  },

  async getListMembers(listId: string): Promise<Array<{ id: string; email: string; name?: string }>> {
    const { data } = await api.get(`/email-campaigns/lists/${listId}/members`)
    return data.data
  },

  async addListMembers(listId: string, contactIds: string[]): Promise<void> {
    await api.post(`/email-campaigns/lists/${listId}/members`, { contact_ids: contactIds })
  },

  async removeListMember(listId: string, contactId: string): Promise<void> {
    await api.delete(`/email-campaigns/lists/${listId}/members/${contactId}`)
  },

  // Email Templates
  async getTemplates(): Promise<EmailTemplate[]> {
    const { data } = await api.get('/email-campaigns/templates')
    return data.data
  },

  async getTemplate(templateId: string): Promise<EmailTemplate> {
    const { data } = await api.get(`/email-campaigns/templates/${templateId}`)
    return data.data
  },

  async createTemplate(templateData: CreateTemplateData): Promise<EmailTemplate> {
    const { data } = await api.post('/email-campaigns/templates', templateData)
    return data.data
  },

  async updateTemplate(templateId: string, templateData: Partial<CreateTemplateData>): Promise<EmailTemplate> {
    const { data } = await api.put(`/email-campaigns/templates/${templateId}`, templateData)
    return data.data
  },

  async deleteTemplate(templateId: string): Promise<void> {
    await api.delete(`/email-campaigns/templates/${templateId}`)
  },

  async duplicateTemplate(templateId: string): Promise<EmailTemplate> {
    const { data } = await api.post(`/email-campaigns/templates/${templateId}/duplicate`)
    return data.data
  },

  // Campaigns
  async getCampaigns(): Promise<EmailCampaign[]> {
    const { data } = await api.get('/email-campaigns/campaigns')
    return data.data
  },

  async getCampaign(campaignId: string): Promise<EmailCampaign> {
    const { data } = await api.get(`/email-campaigns/campaigns/${campaignId}`)
    return data.data
  },

  async createCampaign(campaignData: CreateCampaignData): Promise<EmailCampaign> {
    const { data } = await api.post('/email-campaigns/campaigns', campaignData)
    return data.data
  },

  async updateCampaign(campaignId: string, campaignData: Partial<CreateCampaignData>): Promise<EmailCampaign> {
    const { data } = await api.put(`/email-campaigns/campaigns/${campaignId}`, campaignData)
    return data.data
  },

  async deleteCampaign(campaignId: string): Promise<void> {
    await api.delete(`/email-campaigns/campaigns/${campaignId}`)
  },

  async scheduleCampaign(campaignId: string, scheduledAt: string): Promise<EmailCampaign> {
    const { data } = await api.post(`/email-campaigns/campaigns/${campaignId}/schedule`, {
      scheduled_at: scheduledAt,
    })
    return data.data
  },

  async sendCampaignNow(campaignId: string): Promise<EmailCampaign> {
    const { data } = await api.post(`/email-campaigns/campaigns/${campaignId}/send`)
    return data.data
  },

  async pauseCampaign(campaignId: string): Promise<EmailCampaign> {
    const { data } = await api.post(`/email-campaigns/campaigns/${campaignId}/pause`)
    return data.data
  },

  async cancelCampaign(campaignId: string): Promise<EmailCampaign> {
    const { data } = await api.post(`/email-campaigns/campaigns/${campaignId}/cancel`)
    return data.data
  },

  async getCampaignStats(campaignId: string): Promise<CampaignStats> {
    const { data } = await api.get(`/email-campaigns/campaigns/${campaignId}/stats`)
    return data.data
  },

  // Analytics
  async getAnalytics(params?: { start_date?: string; end_date?: string }): Promise<CampaignAnalytics> {
    const { data } = await api.get('/email-campaigns/analytics', { params })
    return data.data
  },

  // Preview
  async previewEmail(html: string, contactId?: string): Promise<string> {
    const { data } = await api.post('/email-campaigns/preview', { html, contact_id: contactId })
    return data.html
  },

  // Test send
  async sendTestEmail(campaignId: string, email: string): Promise<void> {
    await api.post(`/email-campaigns/campaigns/${campaignId}/test`, { email })
  },
}

export default emailCampaignsApi

// ── Deliverability ──
export interface DomainAuth {
  domain: string
  spf: { ok: boolean; record: string | null; help: string }
  dmarc: { ok: boolean; record: string | null; help: string }
  dkim: { ok: boolean | null; help: string }
}
export async function checkDomainAuth(domain?: string): Promise<DomainAuth> {
  const { data } = await api.get('/email-campaigns/deliverability/domain-auth', { params: domain ? { domain } : {} })
  return data.data
}

export interface AbVariantResult { sent: number; opened: number; clicked: number; open_rate: number; click_rate: number }
export interface AbResults { A: AbVariantResult; B: AbVariantResult; winner: 'A' | 'B' | null; variant_b?: { subject?: string; html_content?: string } | null }
export async function getAbResults(campaignId: string): Promise<AbResults> {
  const { data } = await api.get(`/email-campaigns/campaigns/${campaignId}/ab-results`)
  return data.data
}
